<template><div><h2 id="for-brand" tabindex="-1" class="d-docsite--header-2">For Brand <a class="header-anchor d-link d-docsite--link d-link" href="#for-brand" aria-hidden="true">#</a></h2>
<dt-stack gap="600" class="d-mbs-600">
  <div class="d-d-grid d-g-600 d-g-cols1 md:d-g-cols2 d-ai-center">
    <div>
      <svg-loader name="logo--primary-color-light" class="d-bar-500" />
      <p class="d-docsite--header-3 d-m-0 d-mbs-200">Primary Color Light</p>
    </div>
    <div>
      <svg-loader name="logo--primary-color-dark" class="d-bar-500" />
      <p class="d-docsite--header-3 d-m-0 d-mbs-200">Primary Color Dark</p>
    </div>
  </div>
  <div class="d-d-grid d-g-600 d-g-cols1 md:d-g-cols2 d-ai-center">
    <div>
      <svg-loader name="logo--secondary-color-light" class="d-bar-500" />
      <p class="d-docsite--header-3 d-m-0 d-mbs-200">Secondary Color Light</p>
    </div>
    <div>
      <svg-loader name="logo--secondary-color-dark" class="d-bar-500" />
      <p class="d-docsite--header-3 d-m-0 d-mbs-200">Secondary Color Dark</p>
    </div>
  </div>
  <div class="d-d-grid d-g-600 d-g-cols1 md:d-g-cols3 d-ai-center">
    <div>
      <h2 class="d-docsite--header-3">How to use the logo</h2>
      <p class="d-docsite--paragraph">In almost all cases, the Primary Dialpad logo mark should be used. It consists of the Dialpad icon and word mark.</p>
      <p class="d-docsite--paragraph d-fc-critical">Never use the spark icon on it’s own, it should always be used with the Dialpad wordmark</p>
    </div>
    <div class="d-gc2">
      <svg-loader name="logo--how-to-use" class="d-bar-500" />
    </div>
  </div>
  <div class="d-d-grid d-g-600 d-g-cols1 md:d-g-cols3 d-ai-center">
    <div>
      <h2 class="d-docsite--header-3">Clear space</h2>
      <p class="d-docsite--paragraph">When using the Dialpad logo mark, always use the proper minimum clear space and sizing.</p>
      <h3 class="d-docsite--header-4">Digital Minimum Size </h3>
      <p class="d-docsite--paragraph">
        32 x 64px with clear space<br>
        16 x 48px without clear space
      </p>
    </div>
    <div class="d-gc2">
      <svg-loader name="logo--clear-space" class="d-bar-500" />
    </div>
  </div>
  <div class="d-d-grid d-g-600 d-g-cols1 md:d-g-cols3 d-ai-center">
    <div>
      <h2 class="d-docsite--header-3">Positioning</h2>
      <p class="d-docsite--paragraph">Maintain at least 2x the width of the "d" as clear space around the logo and ensure centering of the "dialpad" wordmark minus the Spark's width.</p>
    </div>
    <div class="d-gc2">
      <svg-loader name="logo--positioning" class="d-bar-500" />
    </div>
  </div>
  <div class="d-d-grid d-g-600 d-g-cols1 md:d-g-cols3 d-ai-center">
    <div>
      <h2 class="d-docsite--header-3">Alignment</h2>
    </div>
    <div class="d-gc2">
      <svg-loader name="logo--alignment" class="d-bar-500" />
    </div>
  </div>
</dt-stack>
<h2 id="in-product" tabindex="-1" class="d-docsite--header-2">In Product <a class="header-anchor d-link d-docsite--link d-link" href="#in-product" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Choose the proper logo variant based on its context and theme.</p>
<code-example only-show="code" source-code='&lt;dt-illustration name=&#39;{variant}&#39; /&gt;'>
<dt-illustration name='{variant}' />
</code-example>
<icons illustration kind="brand-logos" size="large"></icons><h3 id="color-options" tabindex="-1" class="d-docsite--header-3">Color Options <a class="header-anchor d-link d-docsite--link d-link" href="#color-options" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Logos are available in a fixed set of color options to suit different backgrounds, contexts, and theme needs.</p>
<div class="d-ba d-bar-400 d-bc-subtle">
  <table class="d-table dialtone-doc-table">
    <thead>
      <tr>
        <th>Color</th>
        <th>Logo</th>
        <th>Name</th>
        <th>Usage</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td class="d-ws-nowrap">Color</td>
        <td>
          <dt-stack class="d-bgc-transparent d-p-200 d-pie-300 d-bar-400">
            <dt-illustration name='dialpad-logo' />
          </dt-stack>
        </td>
        <td class="d-ws-nowrap">
          <code class="d-code--sm d-docsite-code">dialpad-logo</code>
        </td>
        <td>
          Suitable for most cases. Wordmark foreground color responds to Dialtone light and dark themes. For example, the wordmark will be dark in the default theme, and light in the dark theme.
        </td>
      </tr>
      <tr>
        <td class="d-ws-nowrap">Monochrome</td>
        <td>
          <dt-stack class="d-bgc-transparent d-p-200 d-pie-300 d-bar-400">
            <dt-illustration name='dialpad-mono' />
          </dt-stack>
        </td>
        <td class="d-ws-nowrap">
          <code class="d-code--sm d-docsite-code">dialpad-mono</code>
        </td>
        <td>
          Single-color wordmark and logomark in Dialtone's primary foreground color. Responds to Dialtone light and dark themes.
        </td>
      </tr>
      <tr>
        <td class="d-ws-nowrap">Color</td>
        <td>
          <dt-stack  class="d-bgc-primary-inverted d-p-200 d-pie-300 d-bar-400">
            <dt-illustration name='dialpad-logo-inverted' />
          </dt-stack>
        </td>
        <td class="d-ws-nowrap">
          <code class="d-code--sm d-docsite-code">dialpad-logo-inverted</code>
        </td>
        <td>
          Appropriate for use on a contrasting surface color. For example, in a light theme the wordmark will be light on a dark contrasting surface.
        </td>
      </tr>
      <tr>
        <td class="d-ws-nowrap">Monochrome</td>
        <td>
          <dt-stack class="d-bgc-primary-inverted d-p-200 d-pie-300 d-bar-400">
            <dt-illustration name='dialpad-mono-inverted' />
          </dt-stack>
        </td>
        <td class="d-ws-nowrap">
          <code class="d-code--sm d-docsite-code">dialpad-mono-inverted</code>
        </td>
        <td>
          Appropriate for use on a contrasting surface color. For example, in a light theme it would be light on a dark contrasting surface.
        </td>
      </tr>
      <tr>
        <td class="d-ws-nowrap">Color</td>
        <td>
          <dt-stack  class="d-bgc-neutral-white d-p-200 d-pie-300 d-bar-400">
            <dt-illustration name='dialpad-logo-black' />
          </dt-stack>
        </td>
        <td class="d-ws-nowrap">
          <code class="d-code--sm d-docsite-code">dialpad-logo-black</code>
        </td>
        <td>
          When the wordmark must remain black regardless of theme or context.
        </td>
      </tr>
      <tr>
        <td class="d-ws-nowrap">Monochrome</td>
        <td>
          <dt-stack class="d-bgc-neutral-white d-p-200 d-pie-300 d-bar-400">
            <dt-illustration name='dialpad-mono-black' />
          </dt-stack>
        </td>
        <td class="d-ws-nowrap">
          <code class="d-code--sm d-docsite-code">dialpad-mono-black</code>
        </td>
        <td>
          When the entire logo must remain black regardless of theme.
        </td>
      </tr>
      <tr>
        <td class="d-ws-nowrap">Color</td>
        <td>
          <dt-stack  class="d-bgc-neutral-black d-p-200 d-pie-300 d-bar-400">
            <dt-illustration name='dialpad-logo-white' />
          </dt-stack>
        </td>
        <td class="d-ws-nowrap">
          <code class="d-code--sm d-docsite-code">dialpad-logo-white</code>
        </td>
        <td>
          When the wordmark must remain white regardless of theme or context.
        </td>
      </tr>
      <tr>
        <td class="d-ws-nowrap">Monochrome</td>
        <td>
          <dt-stack class="d-bgc-neutral-black d-p-200 d-pie-300 d-bar-400">
            <dt-illustration name='dialpad-mono-white' />
          </dt-stack>
        </td>
        <td class="d-ws-nowrap">
          <code class="d-code--sm d-docsite-code">dialpad-mono-white</code>
        </td>
        <td>
          When the entire logo must remain white regardless of theme.
        </td>
      </tr>
    </tbody>
  </table>
</div>
</div></template>


